import { Avatar, AvatarFallback } from "../ui/avatar";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  NO_RESULTS_DESCRIPTION,
  NO_RESULTS_TITLE,
  SEARCH_CONVERSATION_DESCRIPTION,
  SEARCH_CONVERSATION_PLACEHOLDER,
  SEARCH_CONVERSATION_TITLE,
} from "@/constants/ui";
import {
  displayMemberName,
  filterMessages,
  formatFullname,
  highlightKeyword,
} from "@/utils/utils";
import {
  selectConversations,
  selectCurrConversation,
} from "@/features/control/controlSlice";
import {
  selectFirstName,
  selectLastName,
  selectUsername,
} from "@/features/auth/authSlice";
import { selectMessages, setSelectedMessage } from "@/features/chat/chatSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MessageType } from "@/types/message";
import { MoveLeft } from "lucide-react";
import { toggleIsSearchView } from "@/features/details/detailsSlice";
import { getMessageTime } from "@/utils/time";

const SearchView = () => {
  const [resultsFound, setResultsFound] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>("");
  const username = useAppSelector(selectUsername);
  const firstName = useAppSelector(selectFirstName) ?? "";
  const lastName = useAppSelector(selectLastName) ?? "";
  const allMessages = useAppSelector(selectMessages);
  const conversations = useAppSelector(selectConversations);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const currConversation = conversations[currConversationIndex!];
  const prevConvIndexRef = useRef<number | null>(currConversationIndex);
  const isGroup = currConversation.type === "Group";
  const [filteredMessages, setFilteredMessages] = useState<MessageType[]>([]);

  const dispatch = useAppDispatch();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setUserInput(e.target.value);
    setResultsFound(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && userInput) {
      const filteredResults = filterMessages(userInput, isGroup, allMessages);
      setFilteredMessages(filteredResults);
      setResultsFound(true);
    }
  };

  const renderResults = useCallback(() => {
    if (filteredMessages.length === 0) {
      return (
        <div className="flex flex-col items-center gap-4 pt-4 text-center">
          <p className="text-sm font-semibold">{NO_RESULTS_TITLE}</p>
          <p className="text-xs">{NO_RESULTS_DESCRIPTION}</p>
        </div>
      );
    }

    return filteredMessages.map((result, index) => {
      const isSent = result.direction === "sent";
      const fullname = isSent
        ? formatFullname(username!, firstName, lastName)
        : displayMemberName(isGroup, result.message, currConversation.fullname);
      const fallbackText = fullname[0].toUpperCase();
      const avatarColor = isSent ? "bg-blue-100" : "";
      const [start, end, parts] = highlightKeyword(
        isGroup,
        userInput,
        result.message
      );
      const selectedMessage = {
        id: result.msgId,
        direction: result.direction,
      };

      const { date, time } = result.created ? getMessageTime(result.created) : { date: "", time: "" };
      const timestamp = date && time ? `${date} · ${time}` : "";

      return (
        <div
          key={index}
          className="flex flex-row items-center gap-2 p-2 rounded-md hover:bg-neutral-200 hover:cursor-pointer transition ease"
          onClick={() => dispatch(setSelectedMessage(selectedMessage))}
        >
          <Avatar className="w-10 h-10 shrink-0">
            <AvatarFallback className={avatarColor}>
              {fallbackText}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5 overflow-auto">
            <div className="flex items-baseline gap-2">
              <p className="text-xs font-medium">{fullname}</p>
              {timestamp && <p className="text-xs text-neutral-400 shrink-0">{timestamp}</p>}
            </div>
            <p className="text-xs">
              {start > 0 && "..."}
              {parts.map((part, index) =>
                part.toLowerCase() === userInput.toLowerCase() ? (
                  <span key={index} className="font-bold">
                    {part}
                  </span>
                ) : (
                  part
                )
              )}
              {end < result.message.length && "..."}
            </p>
          </div>
        </div>
      );
    });
  }, [
    currConversation.fullname,
    dispatch,
    filteredMessages,
    firstName,
    isGroup,
    lastName,
    userInput,
    username,
  ]);

  useEffect(() => {
    setUserInput("");
    setResultsFound(false);
    setFilteredMessages([]);
    if (prevConvIndexRef.current !== currConversationIndex) {
      dispatch(toggleIsSearchView());
      prevConvIndexRef.current = currConversationIndex;
    }
  }, [currConversationIndex, dispatch]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-row items-center gap-2">
        <Button
          variant="ghost"
          className="px-2 py-0 h-9"
          onClick={() => dispatch(toggleIsSearchView())}
        >
          <MoveLeft size={24} />
        </Button>
        <p className="text-xl font-medium">{SEARCH_CONVERSATION_TITLE}</p>
      </div>
      <Input
        value={userInput}
        placeholder={SEARCH_CONVERSATION_PLACEHOLDER}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleInputChange(e)
        }
        onKeyDown={handleKeyDown}
      />
      {userInput && !resultsFound && (
        <p className="self-center pt-5 text-xs">
          {SEARCH_CONVERSATION_DESCRIPTION}
        </p>
      )}

      {resultsFound && renderResults()}
    </div>
  );
};

export default SearchView;
